import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { DayPickerInput, SingleSelect } from '~/components';
import { useDocumentTitle } from '~/hooks';
import { colors, weights } from '~/styles';
import { dateFormats } from '~/utils';
import { Header, Page } from '../../components';
import { Section, SectionTitle } from './shared';
import Summary from './Summary';
import Conversations from './Conversations';
import CostSummary from './CostSummary';
import Concentration from './Concentration';
import CostByModel from './CostByModel';
import ToolCalls from './ToolCalls';
import Members from './Members';
import Workspaces from './Workspaces';

const tabs = ['activity', 'cost', 'workspaces', 'members', 'tooling'];

const Filters = styled.div`
  display: flex;
`;

const Filter = styled.div`
  display: flex;
  align-items: center;

  &:not(:first-child) {
    margin-left: 1.5rem;
  }
`;

const TabBar = styled.ul`
  display: flex;
  margin-top: 1.5rem;
  border-bottom: solid 1px ${colors.grey10};
`;

const Tab = styled.li`
  padding: 0.75rem 1.5rem;
  font-weight: ${({ isActive }) => (isActive ? weights.bold : weights.normal)};
  color: ${({ isActive }) => (isActive ? colors.black : colors.grey50)};
  border-bottom: solid 2px ${({ isActive }) => (isActive ? colors.primary : 'transparent')};
  cursor: pointer;

  &:hover {
    color: ${colors.black};
  }
`;

const Content = styled.div`
  margin-top: 2rem;
`;

export default function AiDashboard() {
  useDocumentTitle('AI Dashboard - Admin');

  const history = useHistory();
  const route = useRouteMatch('/admin/ai/dashboard/:tab?');
  const routeTab = route?.params?.tab;
  const [tab, setTab] = useState();

  useEffect(() => {
    if (!routeTab || !tabs.includes(routeTab)) {
      setTab('activity');
      history.replace('/admin/ai/dashboard/activity');
    } else {
      setTab(routeTab);
    }
  }, [history, routeTab]);

  useEffect(() => {
    if (tab === undefined || window.location.pathname === `/admin/ai/dashboard/${tab}`) return;
    history.push(`/admin/ai/dashboard/${tab}`);
  }, [history, tab]);

  const [timespan, setTimespan] = useState('last-30');
  const [granularity, setGranularity] = useState('day');
  const [start, setStart] = useState(moment().subtract(30, 'days').format(dateFormats.isoDate));
  const [end, setEnd] = useState(moment().format(dateFormats.isoDate));

  useEffect(() => {
    if (timespan === 'last-30') {
      setStart(moment().subtract(30, 'days').format(dateFormats.isoDate));
      setEnd(moment().format(dateFormats.isoDate));
    }
  }, [timespan]);

  return (
    <Page>
      <Header />
      <Filters>
        <Filter style={{ minWidth: '12rem' }}>
          <SingleSelect placeholder="Timespan" value={timespan} onChange={(e) => setTimespan(e.target.value)}>
            <option value="last-30">Last 30 Days</option>
            <option value="custom">Custom</option>
          </SingleSelect>
        </Filter>
        {timespan === 'custom' && (
          <>
            <Filter>
              <DayPickerInput placeholder="Start" value={start} onChange={(value) => setStart(value ?? undefined)} />
            </Filter>
            <Filter>
              <DayPickerInput placeholder="End" value={end} onChange={(value) => setEnd(value ?? undefined)} />
            </Filter>
          </>
        )}
        {timespan !== 'custom' && (
          <Filter>
            {!!start && moment(start).format(dateFormats.compactDate)} -{' '}
            {!!end && moment(end).format(dateFormats.compactDate)}
          </Filter>
        )}
        {(tab === 'activity' || tab === 'cost') && (
          <Filter style={{ minWidth: '10rem' }}>
            <SingleSelect
              placeholder="Granularity"
              value={granularity}
              onChange={(e) => setGranularity(e.target.value)}>
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </SingleSelect>
          </Filter>
        )}
      </Filters>
      <TabBar>
        <Tab isActive={tab === 'activity'} onClick={() => setTab('activity')}>
          Activity
        </Tab>
        <Tab isActive={tab === 'cost'} onClick={() => setTab('cost')}>
          Cost
        </Tab>
        <Tab isActive={tab === 'workspaces'} onClick={() => setTab('workspaces')}>
          Workspaces
        </Tab>
        <Tab isActive={tab === 'members'} onClick={() => setTab('members')}>
          Members
        </Tab>
        <Tab isActive={tab === 'tooling'} onClick={() => setTab('tooling')}>
          Tooling
        </Tab>
      </TabBar>
      <Content>
        {tab === 'activity' && (
          <>
            <Section>
              <Summary start={start} end={end} granularity={granularity} />
            </Section>
            <Conversations start={start} end={end} granularity={granularity} />
          </>
        )}
        {tab === 'cost' && (
          <>
            <CostSummary start={start} end={end} granularity={granularity} />
            <SectionTitle>Cost Distribution</SectionTitle>
            <Section>
              <Concentration start={start} end={end} />
            </Section>
            <Section>
              <CostByModel start={start} end={end} />
            </Section>
          </>
        )}
        {tab === 'workspaces' && <Workspaces start={start} end={end} />}
        {tab === 'members' && <Members start={start} end={end} />}
        {tab === 'tooling' && <ToolCalls start={start} end={end} />}
      </Content>
    </Page>
  );
}
